/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2018 ICEMEDX, Inc.
***********************************************/
import { ViewChild, Input, Output, EventEmitter, Inject} from '@angular/core';
import { IcepanelComponent } from './icepanel.component';
import { MatTableDataSource } from '@angular/material';
import { MatPaginator } from '@angular/material';
import { iceColumnDefinition, IcemattableSettings } from 'projects/icemedx-lib/src/components/models/icesettings';
import { IceTypes } from '../../models/icetypes';
import { logmessage } from '../../models/system/logmessage';
import { FormGroup } from '@angular/forms';
import { Iceutility } from '../../services/iceutility';

export interface IIcetablecomponent {
  onRowClick(event, rowData);
  onColumnClick(event, colDef, rowData);
  onRowActionButtonClick(event, item: any, rowData: any);
  onAddRowButtonClick(event);
  hasAccess();
}

export class tablebasecomponent implements IIcetablecomponent {
  // Input members
  @Input() actionItems: string[] = [];
  @Input() doInlineEdit: boolean;
  @Input() enableRowSelection: boolean;
  @Input() iColumns: any = [];
  @Input() iRows: MatTableDataSource<any> = new MatTableDataSource<any>();
  @Input() matgridTag: string;
  @Input() minimalGrid: boolean;
  @Input() hidePaginator: boolean;
  @Input() addButton: boolean;
  @Input() showTable: boolean = true;
  @Input() ctrl: IcemattableSettings = null;
  @Input() loadingIndicator: boolean;

  // Child members
  @ViewChild(MatPaginator) paginator: MatPaginator;

  // Output members
  @Output() rowClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() tableLazyLoad: EventEmitter<any> = new EventEmitter<any>();
  @Output() controlChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() colClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() rowActionClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() addRowButtonClick: EventEmitter<any> = new EventEmitter<any>();

  selectedRowData: any = null;

  noRecordsCaption: string = "No records found";
  rowData: any = null;

  constructor(
    @Inject(IcepanelComponent) private parent: IcepanelComponent) {
  }

  getParentPanel(): IcepanelComponent {
    return this.parent;
  }

  getData(colName: string, rowData: any) {
    try {
      if (!colName || colName.length == 0) throw "Error: No column specified in iColumns definition.";
      if (!rowData) throw "Error: No row data found in iRows.";
      return rowData[colName];
    }
    catch (e) {
      return "";
    }
  }

  getFormGroup(rowData: any): FormGroup {
    let formGrp: FormGroup = null;

    try {
      if (this.ctrl == null || rowData == null) { // no record so create group to prevent compilation errors
        return new FormGroup({});
      }

      let createCtrls = false;
      if (this.ctrl.iColumns != null && this.ctrl.iColumns.length > 0) {
        for (let icol = 0; icol < this.ctrl.iColumns.length; icol++) {
          if (this.ctrl.iColumns[icol].editProperties != null) {
            createCtrls = true;
            break;
          }
        }
      }
      return this.ctrl.getFormGroup(rowData, createCtrls);
    }
    catch (e) {
      console.log(e);
    }

    return formGrp;
  }

  getPrimaryKey(): string {
    if (this.ctrl == null) return "";
    return this.ctrl.getPrimaryKey();
  }

  getDisplayName(colName: string): string {
    if (this.ctrl == null) return "";
    return this.ctrl.getDisplayName(colName);
  }

  getColumns(): iceColumnDefinition[] {
    if (this.ctrl == null) return [];
    return this.ctrl.getColumns();
  }

  getColumnNames(): string[] {
    if (this.ctrl == null) return [];
    return this.ctrl.getColumnNames();
  }

  getButtonClass(column: iceColumnDefinition, rowData: any) {
    if (column == null || column.editProperties == null || column.editProperties.properties == null || rowData == null)
      return { '': true };

    let cls = rowData[column.columnName];
    if (column.editProperties.tag == 'icebutton')
      return cls;

    return column.editProperties.properties.class ? column.editProperties.properties.class : '';
  }

  getQuickActionItems(): any[] {
    return this.ctrl.actionItems;
  }

  getQuickActionClass(item: any): any {
    if (item == null)
      return { "fas": true, "fa-question": true, "fa-2x": true };

    return item.iconClass;
  }

  getLogMessageClass(rowData: logmessage) {
    try {
      switch (parseInt(rowData.logtype.toString())) {
        case IceTypes.LOGTYPE.ltWarning: {
          return "alert alert-warning";
        }
        case IceTypes.LOGTYPE.ltUserAlert: {
          return "alert alert-success";
        }
        case IceTypes.LOGTYPE.ltMedicalAlert:
        case IceTypes.LOGTYPE.ltError:
        case IceTypes.LOGTYPE.ltFatal:
        case IceTypes.LOGTYPE.ltSecurity: {
          return "alert alert-danger";
        }
        case IceTypes.LOGTYPE.ltBusinessIntel:
        default: {
          return "alert alert-info";
        }
      }
    }
    catch (e) {
      return 0;
    }
  }

  getColumnStyle(column: iceColumnDefinition) {
    let style = {
      width: '100%',
      color: 'black',
    };

    style["padding-left"] = "10px";
    if (column == null || column.editProperties == null || column.editProperties.properties == null)
      return "";

    if (column.editProperties.properties["width"] != null)
      style.width = column.editProperties.properties["width"];

    if (column.editProperties.properties["color"] != null)
      style.color = column.editProperties.properties["color"];

    if (column.editProperties.properties["cursor"] != null) {
      style["cursor"] = column.editProperties.properties["cursor"];
    }

    return style;
  }

  onTableScroll(event) {
    const tableViewHeight = event.target.offsetHeight // viewport: ~500px
    const tableScrollHeight = event.target.scrollHeight // length of all table
    const scrollLocation = event.target.scrollTop; // how far user scrolled

    // If the user has scrolled within 200px of the bottom, add more data
    const buffer = 200;
    const limit = tableScrollHeight - tableViewHeight - buffer;
    if (scrollLocation > limit) {
      if (this.parent && this.parent.tableLazyLoad)
        this.parent.onTableLazyLoad(event);
      else
        this.tableLazyLoad.emit(event);
    }
  }

  onControlChange(event) {
    if (this.parent && this.parent.controlChange)
      this.parent.onControlChange(event);
    else
      this.controlChange.emit(event);
  }

  removeLogMessage(index: number) {
    this.iRows.data.splice(index, 1);
  }

  onRowClick(event, rowData) {
  }

  onColumnClick(event, colDef, rowData) {
  }

  onRowActionButtonClick(event, item: any, rowData: any) {
  }

  onAddRowButtonClick(event) {
  }

  hasAccess() {
  }

  isMobileDevice(): boolean {
    return Iceutility.isMobileDevice();
  }

  setCurrentRowData(data) {
    this.rowData = data;
  }

  showNoDataMessage() {
    if (!Iceutility.isdefined(this.iRows)) {
      return true;
    } else if (!Iceutility.isdefined(this.iRows.data)) {
      return true;
    }
    return this.iRows.data.length === 0;
  }
}
